"use client";

import type { ReactNode } from "react";
import { Check, ChevronRight, Lock, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SpotlightCtaView, SpotlightView } from "@/contexts/feed/domain/spotlight/spotlight-settings";
import { SpotlightDialog } from "./spotlight-dialog";
import { GlassCardStrong } from "@/shared/ui/glass";
import { HERO_CONTAIN_AREA, INK, MUTED, SpotlightEyebrow, SpotlightHeroMedia, SpotlightPrimaryButton, SpotlightQuietButton } from "./spotlight-parts";

/**
 * SHOWCASE: a wide split, the clip carries the left, the argument the right.
 *
 *   ┌─────────────────────────── 1060 ──────────────────────────┐
 *   │ ┌──────── hero 66% ────────┐                          (x) │
 *   │ │                          │  FORMS · NEW ON COVA          │
 *   │ │                          │  Headline                     │
 *   │ │                          │  Summary                      │
 *   │ │                          │  ✓ A client's form history    │
 *   │ └──────────────────────────┘  ✓ Published form inbox       │
 *   │                               [ Open Forms → ]  Not now    │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ Up next  Microsoft 365: connect Outlook even when…      ›  │
 *   └───────────────────────────────────────────────────────────┘
 *
 * The hero area is 5:4 and the picture or clip sits centred in it at its own
 * shape, never cropped. Under 640px it stacks (hero, words, list, actions) and
 * scrolls inside, the hero at the media's own height.
 *
 * `renderSlot` is for the composer's Stage only: it wraps each region in an
 * edit affordance so the author edits THIS component at true size. Brokers
 * never pass it, and without it every slot renders exactly as below. The
 * button is the server's resolution for a broker, and the composer's own
 * `resolveCtaState` for "Preview as"; a skip is drawn only on the Stage.
 */
export type ShowcaseSlot = "hero" | "headline" | "summary" | "highlights" | "cta";

/** The button as the Showcase draws it: the wire's three, plus the Stage's skip. */
export type ShowcaseCta = SpotlightCtaView | {
  readonly kind: "skip";
  readonly reason: string;
};
const asIs = (_slot: ShowcaseSlot, node: ReactNode) => node;
export function SpotlightShowcase({
  spotlight,
  cta = spotlight.cta,
  next,
  reduced,
  inline = false,
  busy = false,
  onDismiss,
  onAct,
  onNext,
  renderSlot = asIs
}: {
  spotlight: SpotlightView;
  /** Overrides the spotlight's own button: the Stage's "Preview as". */
  cta?: ShowcaseCta;
  /** The second spotlight in the sitting, named in the "Up next" strip. */
  next?: SpotlightView | null;
  reduced: boolean;
  /** The Stage: positioned inside the composer, not portalled. */
  inline?: boolean;
  busy?: boolean;
  onDismiss: () => void;
  onAct: () => void;
  /** "Up next" dismisses this one; the next follows if the sitting allows. */
  onNext?: () => void;
  renderSlot?: (slot: ShowcaseSlot, node: ReactNode) => ReactNode;
}) {
  return <SpotlightDialog label={spotlight.headline} inline={inline} reduced={reduced} onEscape={onDismiss}>
      {/* The feed card's frame: the frosted GlassCardStrong surface with its
          border, padding all round, and the hero inset in its own rounded,
          ringed box. The fill is raised to /90 because it sits over a dimmed
          page (the frosted-glass cap) and the headline has to hold. */}
      {/* The outer frame: a 6px pale frosted mat around the card, like a photo
          mount, with the scrim only faintly through it, and a thin quiet line
          on its edge, concentric with the card (radius 16 + 6). Solid and
          neutral, because dashed brand purple is editing chrome. 1060 wide
          including the frame. */}
      <div key={spotlight.postId} className="relative flex max-h-full w-full max-w-[1060px] rounded-[22px] border border-black/8 bg-white/70 p-1.5 backdrop-blur-xl dark:border-white/15 dark:bg-[#15171c]/70">
        <GlassCardStrong className={cn("relative flex max-h-full w-full flex-col overflow-hidden rounded-2xl bg-white/90 p-5 hover:shadow-[0_40px_120px_-30px_rgba(9,30,66,0.55)] sm:p-6", "shadow-[0_40px_120px_-30px_rgba(9,30,66,0.55)] dark:bg-[#15171c]/92", !reduced && "animate-in fade-in-0 zoom-in-95 duration-500")}>
          <button type="button" onClick={onDismiss} aria-label="Close" className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-[#091E42] shadow-xs backdrop-blur-md transition hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5651ff] sm:bg-transparent sm:text-gray-400 sm:shadow-none sm:backdrop-blur-none sm:hover:bg-black/4 sm:hover:text-[#091E42] dark:sm:hover:bg-white/6 dark:sm:hover:text-white">
            <X className="h-4 w-4" />
          </button>

          <div className="min-h-0 overflow-y-auto sm:grid sm:grid-cols-[66fr_34fr] sm:gap-8 sm:overflow-visible">
            {renderSlot("hero", <div className={cn(HERO_CONTAIN_AREA, "self-center")} data-testid="spotlight-hero-area">
                <SpotlightHeroMedia hero={spotlight.hero} reduced={reduced} fit="contain" />
              </div>)}

            <div className="flex flex-col pt-5 sm:py-2 sm:pr-6 sm:pt-2">
              <SpotlightEyebrow categoryName={spotlight.categoryName} categoryColor={spotlight.categoryColor} />
              {renderSlot("headline", <h2 className={cn("mt-2.5 text-balance text-[21px] font-semibold leading-[1.2] tracking-[-0.02em] sm:text-[23px] sm:leading-[1.15]", INK)}>
                  {spotlight.headline}
                </h2>)}
              {renderSlot("summary", <p className={cn("mt-2.5 text-pretty text-sm leading-relaxed", MUTED)}>{spotlight.summary}</p>)}

              {renderSlot("highlights", spotlight.highlights.length > 0 ? <ul className="mt-5 flex flex-col gap-3">
                    {spotlight.highlights.map((item, i) => <li key={`${i}-${item.title}`} className={cn("flex gap-3", !reduced && "animate-in fade-in-0 slide-in-from-left-2 fill-mode-both")} style={reduced ? undefined : {
                animationDelay: `${250 + i * 90}ms`,
                animationDuration: "450ms"
              }}>
                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#5651ff]/10 text-[#5651ff]">
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                        <span className="text-sm leading-snug">
                          <span className={cn("font-semibold", INK)}>{item.title}</span>
                          <span className={cn("block", MUTED)}>{item.detail}</span>
                        </span>
                      </li>)}
                  </ul> : null)}

              <div className="mt-auto flex flex-col items-stretch gap-2 pt-6 sm:flex-row sm:flex-wrap sm:items-center">
                {renderSlot("cta", cta.kind === "skip" ? <span className="inline-flex h-10 items-center rounded-full px-4 text-sm text-gray-400 ring-1 ring-gray-300">
                      Skipped: {cta.reason}
                    </span> : <SpotlightPrimaryButton label={cta.label} onClick={onAct} disabled={busy} autoFocus />)}
                <SpotlightQuietButton label="Not now" onClick={onDismiss} disabled={busy} />
              </div>
              {(cta.kind === "request" || cta.kind === "upgrade") && <p className={cn("mt-2.5 flex items-start gap-1.5 text-xs leading-relaxed", MUTED)}>
                  <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                  {cta.note}
                </p>}
            </div>
          </div>

          {next && <button type="button" onClick={onNext ?? onDismiss} disabled={busy} className="group mt-5 flex shrink-0 items-center gap-3 rounded-xl bg-[#F7F8F9]/80 px-4 py-3 text-left ring-1 ring-black/6 transition hover:bg-[#f3f1ff] dark:bg-white/3 dark:ring-white/10 dark:hover:bg-white/6">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">Up next</span>
              <span className={cn("min-w-0 flex-1 truncate text-sm font-medium", INK)}>{next.headline}</span>
              <ChevronRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5" />
            </button>}
        </GlassCardStrong>
      </div>
    </SpotlightDialog>;
}